import React from 'react'
import { Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import {
  Bullet,
  Bullets,
  Info,
  Screenshot,
  Warning,
} from '../../components/DocsComponents'

const DbsDrivingLicenceId = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">
        DBS, driving licence, and ID compliance
      </Typography>
      <Typography>
        AmbuNet tracks three identity and vetting records against every
        employee: their DBS check (with automated Update Service monitoring),
        their driving licence and licence checks, and their staff ID cards.
        Everything surfaces on the employee record and rolls up to the
        Workforce Dashboard's Action Centre, so lapses never rely on someone
        remembering a date.
      </Typography>
      <Info title="Who can do this?">
        The DBS dashboard, driving licence checks, and ID cards all require
        the <strong>Personnel</strong> team. Site administrators always have
        access.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">The DBS Monitoring dashboard</Typography>
      <Typography>
        <DocLink text="DBS Checks" url="/dbs-checks" /> (People &gt; DBS
        Checks) shows your whole DBS position at a glance, with summary cards
        for:
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Current</strong> — checks the Update Service reports as
          current, including those where the original certificate carried
          information.
        </Bullet>
        <Bullet>
          <strong>New Information</strong> — the Update Service has flagged
          new information: a new DBS certificate is needed.
        </Bullet>
        <Bullet>
          <strong>Unable to Check</strong> — certificate details that could
          not be checked or matched, including monitored records with no
          certificate number.
        </Bullet>
        <Bullet>
          <strong>Not on Update Service</strong> — manual DBS records plus
          staff with no DBS recorded at all.
        </Bullet>
      </Bullets>
      <Typography>
        Below the cards, a single worklist collects everything needing
        attention — Update Service results needing action, manual certificates
        that have lapsed or were never completed, and staff with no DBS
        recorded — each with a recommended action and a link to the employee
        record. Manual certificates count as current for the number of months
        set in your organisation's DBS settings (36 by default).
      </Typography>
      <Screenshot url="employees/dbs-dashboard.png">
        The DBS monitoring dashboard with its summary cards and the worklist of
        records needing attention.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Automated Update Service monitoring
      </Typography>
      <Typography>
        DBS records whose holder is enrolled on the DBS Update Service are
        re-checked automatically every night, and the dashboard shows when the
        checks last ran. A record is treated as monitored unless monitoring
        has been explicitly switched off on it — so records that have never
        had the toggle set are checked rather than silently skipped.
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Failed checks list</strong> — records whose last automated
          check failed (invalid certificate number, date of birth mismatch, no
          match found, missing data) are listed separately so each can be
          corrected on the employee's DBS record.
        </Bullet>
        <Bullet>
          <strong>Run checks now</strong> — re-runs the Update Service checks
          for your whole organisation on demand, instead of waiting for the
          overnight run.
        </Bullet>
      </Bullets>
      <Warning title="A monitored record needs a certificate number">
        A monitored record with no 12-digit certificate number can never be
        checked — the dashboard counts these as monitored-but-not-clear rather
        than letting them drop out of view. Add the certificate number, or
        switch monitoring off if the person is not subscribed to the Update
        Service.
      </Warning>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Driving licences</Typography>
      <Typography>
        Driving licence details — licence number, categories, entitlements
        such as C1 and blue light, and endorsements — live on the employee
        record's Details tab, with periodic licence checks recorded against
        them. The check frequency comes from your staff governance settings
        (every 12 months by default), and overdue checks appear in the
        Workforce Dashboard's Action Centre.
      </Typography>
      <Info title="Who counts as a driver">
        An employee with a licence number recorded is treated as a driver and
        is expected to have in-date licence checks. Non-drivers simply have no
        licence recorded — there is no separate driver flag, so don't enter a
        licence number for staff who will never drive.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">ID cards</Typography>
      <Typography>
        Staff ID cards are issued from the employee record, each with an issue
        and expiry date and a record of who issued it. Missing ID cards are
        flagged in the Workforce Dashboard's Action Centre alongside the
        other compliance gaps.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Related documentation</Typography>
      <Typography>
        <DocLink
          text="Workforce Dashboard"
          url="/employees-hr/personnel-dashboard"
          docsPage
        />
        <DocLink
          text="Managing Employees"
          url="/employees-hr/managing-employees"
          docsPage
        />
        <DocLink
          text="Recruitment & Applications"
          url="/employees-hr/recruitment-applications"
          docsPage
        />
      </Typography>
    </Stack>
  </Stack>
)

export default DbsDrivingLicenceId
